import React from 'react';

const ProcessingModal = ({ 
  isOpen, 
  serviceName = "your request", 
  message = null, 
  bookTitle = null, 
  progressSteps = null,
  currentStep = null,
  progress = 0,
  pricingInfo = null, // { amount: 5.00, isFree: false, userType: 'subscriber' }
  userRole = null
}) => {
  if (!isOpen) return null;

  const serviceDisplayNames = {
    // Lowercase keys
    bctg: "Book Concept & Title Generator (BCTG)",
    bvadm: "Book Viability & Digital Marketing Analysis (BVADM)",
    bocp: "Book Outline & Chapter Planner (BOCP)",
    mmg: "Metadata & Marketing Generator (MMG)",
    ccg: "Character & Cast Generator (CCG)",
    ccat: "Competitor & Comparative Analysis Tool (CCAT)",
    brec: "Book Review & Endorsement Creator (BREC)",
    abg: "Author Biography Generator (ABG)",
    ksg: "Keynote Speech Generator (KSG)",
    aksw: "Amazon KDP Submission Wizard (AKSW)",
    mmkg: "Manuscript Media Kit Generator (MMKG)",
    // Full name variations
    "Book Concept & Title Generator (BCTG)": "Book Concept & Title Generator (BCTG)",
    "Book Viability & Digital Marketing Analysis (BVADM)": "Book Viability & Digital Marketing Analysis (BVADM)",
    "Book Viability & Digital Marketing (BVADM)": "Book Viability & Digital Marketing Analysis (BVADM)",
    "Book Viability and Development Model (BVADM)": "Book Viability & Digital Marketing Analysis (BVADM)",
    "Book Outline & Chapter Planner (BOCP)": "Book Outline & Chapter Planner (BOCP)",
    "Book Outline and Chapter Planner (BOCP)": "Book Outline & Chapter Planner (BOCP)",
    "Metadata & Marketing Generator (MMG)": "Metadata & Marketing Generator (MMG)",
    "Manuscript Metadata Generator (MMG)": "Metadata & Marketing Generator (MMG)",
    "Character & Cast Generator (CCG)": "Character & Cast Generator (CCG)",
    "Competitor & Comparative Analysis Tool (CCAT)": "Competitor & Comparative Analysis Tool (CCAT)",
    "Book Review & Endorsement Creator (BREC)": "Book Review & Endorsement Creator (BREC)",
    "Author Biography Generator (ABG)": "Author Biography Generator (ABG)",
    "Keynote Speech Generator (KSG)": "Keynote Speech Generator (KSG)",
    // Acronym only
    "BCTG": "Book Concept & Title Generator (BCTG)",
    "BVADM": "Book Viability & Digital Marketing Analysis (BVADM)",
    "BVADM Analysis": "Book Viability & Digital Marketing Analysis (BVADM)",
    "BOCP": "Book Outline & Chapter Planner (BOCP)",
    "MMG": "Metadata & Marketing Generator (MMG)",
    "CCG": "Character & Cast Generator (CCG)",
    "CCAT": "Competitor & Comparative Analysis Tool (CCAT)",
    "BREC": "Book Review & Endorsement Creator (BREC)",
    "ABG": "Author Biography Generator (ABG)",
    "KSG": "Keynote Speech Generator (KSG)",
    "AKSW": "Amazon KDP Submission Wizard (AKSW)",
    "MMKG": "Manuscript Media Kit Generator (MMKG)",
    "Amazon KDP Submission Wizard (AKSW)": "Amazon KDP Submission Wizard (AKSW)",
    "Manuscript Media Kit Generator (MMKG)": "Manuscript Media Kit Generator (MMKG)",
    // Generic
    "Service": "AI Service"
  };

  const displayServiceName = serviceDisplayNames[serviceName] || serviceName;
  
  // Create message with book title if provided
  let displayMessage;
  if (message) {
    displayMessage = message;
  } else if (bookTitle) {
    displayMessage = `Your Digital Ink AI powered ${displayServiceName} for "${bookTitle}" is now being created. Please standby.`;
  } else {
    displayMessage = `Your Digital Ink AI powered ${displayServiceName} is now being created. Please standby.`;
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm p-2 sm:p-4">
      <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 max-w-md w-full shadow-2xl animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <img 
            src="/Digital_Ink_AI_Logo_Gold.png" 
            alt="Digital Ink AI" 
            className="h-12 sm:h-16 w-auto object-contain"
          />
        </div>

        {/* Animated icon */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="relative">
            <div className="w-16 h-16 sm:w-20 sm:h-20 border-4 border-blue-200 rounded-full"></div>
            <div className="w-16 h-16 sm:w-20 sm:h-20 border-4 border-blue-600 rounded-full absolute top-0 left-0 animate-spin border-t-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center text-2xl sm:text-3xl">
              ✨
            </div>
          </div>
        </div>

        {/* Service Name */}
        <h3 className="text-lg sm:text-xl font-bold text-center text-slate-900 mb-1">
          {displayServiceName}
        </h3>

        {/* AI Processing subtitle */}
        <p className="text-center text-slate-500 text-sm mb-4">
          AI Processing in Progress
        </p>

        {/* Progress Bar */}
        <div className="mb-4 sm:mb-6">
          <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            ></div>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-slate-500">{Math.round(progress)}% Complete</span>
            <span className="text-xs text-slate-500">
              {(() => {
                const key = serviceName.toLowerCase();
                if (key.includes('bvadm') || key.includes('bocp') || key.includes('ksg') || key.includes('aksw') || key.includes('brec') || key.includes('ccat') || key.includes('mmg')) {
                  return '2-3 min';
                } else if (key.includes('mmkg')) {
                  return '4-5 min';
                } else if (key.includes('smmk')) {
                  return '3-4 min';
                } else if (key.includes('bctg') || key.includes('ccg') || key.includes('abg')) {
                  return '1-2 min';
                } else {
                  return '2-3 min';
                }
              })()}
            </span>
          </div>
        </div>

        {/* Pricing Information */}
        {pricingInfo && (
          <div className={`mb-4 p-3 rounded-lg border-2 ${
            pricingInfo.isFree 
              ? 'bg-green-50 border-green-300' 
              : 'bg-blue-50 border-blue-300'
          }`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">Service Cost:</span>
              <span className={`text-base font-bold ${
                pricingInfo.isFree ? 'text-green-700' : 'text-blue-700'
              }`}>
                {pricingInfo.isFree ? (
                  <>
                    ✓ FREE
                    {pricingInfo.userType && (
                      <span className="text-xs font-normal ml-1">
                        ({pricingInfo.userType === 'subscriber' ? 'Subscriber Benefit' : 
                          pricingInfo.userType === 'admin' ? 'Admin Access' :
                          pricingInfo.userType === 'freemium' ? 'Freemium' : 'Free Access'})
                      </span>
                    )}
                  </>
                ) : (
                  `$${pricingInfo.amount.toFixed(2)}`
                )}
              </span>
            </div>
            {!pricingInfo.isFree && pricingInfo.userType === 'subscriber' && pricingInfo.originalAmount && (
              <div className="mt-1 text-xs text-slate-600 text-right">
                <span className="line-through">${pricingInfo.originalAmount.toFixed(2)}</span>
                <span className="ml-1 text-green-600 font-semibold">50% Subscriber Discount</span>
              </div>
            )}
          </div>
        )}

        {/* Current Step Indicator */}
        {currentStep && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
              <p className="text-sm font-medium text-blue-900">{currentStep}</p>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="space-y-2 bg-slate-50 rounded-lg p-4">
          <p className="text-xs font-semibold text-slate-600 mb-3 uppercase tracking-wide">Processing Steps</p>
          
          {progressSteps && progressSteps.length > 0 ? (
            // Dynamic progress steps from service
            <>
              {progressSteps.map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    step.completed 
                      ? 'bg-green-500 text-white' 
                      : step.current
                      ? 'bg-blue-500 text-white animate-pulse'
                      : 'bg-slate-300 text-slate-500'
                  }`}>
                    {step.completed ? '✓' : index + 1}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <p className={`text-sm ${
                      step.completed 
                        ? 'text-green-700 font-medium' 
                        : step.current
                        ? 'text-blue-900 font-semibold'
                        : 'text-slate-500'
                    }`}>
                      {step.text}
                    </p>
                    {step.current && step.subtext && (
                      <p className="text-xs text-slate-500 mt-1">{step.subtext}</p>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : serviceName === 'bocp' || serviceName === 'Book Outline and Chapter Planner (BOCP)' ? (
            // BOCP specific steps
            <>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold animate-pulse">1</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-blue-900 font-semibold">Generating Table of Contents</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-300 text-slate-500 flex items-center justify-center text-xs font-bold">2</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-slate-500">Creating detailed chapter outlines</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-300 text-slate-500 flex items-center justify-center text-xs font-bold">3</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-slate-500">Adding strategic guidance</p>
                </div>
              </div>
            </>
          ) : serviceName.toLowerCase().includes('aksw') ? (
            // AKSW specific steps
            <>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold animate-pulse">1</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-blue-900 font-semibold">Analyzing your manuscript</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-300 text-slate-500 flex items-center justify-center text-xs font-bold">2</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-slate-500">Generating Amazon KDP metadata</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-300 text-slate-500 flex items-center justify-center text-xs font-bold">3</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-slate-500">Creating submission guide</p>
                </div>
              </div>
            </>
          ) : serviceName.toLowerCase().includes('mmkg') ? (
            // MMKG specific steps
            <>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold animate-pulse">1</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-orange-900 font-semibold">Analyzing manuscript intelligence</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-300 text-slate-500 flex items-center justify-center text-xs font-bold">2</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-slate-500">Generating publicity materials</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-300 text-slate-500 flex items-center justify-center text-xs font-bold">3</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-slate-500">Creating media kit components</p>
                </div>
              </div>
            </>
          ) : (
            // Generic steps for all other services
            <>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold animate-pulse">1</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-blue-900 font-semibold">Analyzing your input</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-300 text-slate-500 flex items-center justify-center text-xs font-bold">2</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-slate-500">Generating AI insights</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-300 text-slate-500 flex items-center justify-center text-xs font-bold">3</div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm text-slate-500">Formatting results</p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Important Notice */}
        <div className="mt-4 sm:mt-6 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-xs text-amber-900 text-center font-medium flex items-center justify-center gap-2">
            <span className="text-base">⚠️</span>
            <span>Please don't close this window during processing</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProcessingModal;
